import React, { useState, useEffect, useRef } from 'react';
import { Shield, Code, TrendingUp, Building2, Globe } from 'lucide-react';

const BinaryRainBackground = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);
    
    const fontSize = 10;
    const columns = canvas.width / fontSize;
    const drops = Array(Math.floor(columns)).fill(1);
    const chars = ['0', '1'];
    
    const draw = () => {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.font = `${fontSize}px monospace`;
      
      drops.forEach((drop, i) => {
        const char = chars[Math.floor(Math.random() * chars.length)];
        
        if (char === '1') {
          const blueIntensity = Math.floor(Math.random() * 55 + 200);
          ctx.fillStyle = `rgba(${blueIntensity}, ${blueIntensity + 20}, 255, 0.4)`;
        } else {
          const blueIntensity = Math.floor(Math.random() * 55 + 100);
          ctx.fillStyle = `rgba(${blueIntensity}, ${blueIntensity + 20}, 255, 0.15)`;
        }
        
        ctx.fillText(char, i * fontSize, drop * fontSize);
        
        if (drop * fontSize > canvas.height && Math.random() > (char === '1' ? 0.98 : 0.96)) {
          drops[i] = 0;
        }
        drops[i] += char === '1' ? 1.2 : 0.8;
      });
    };
    
    const interval = setInterval(draw, 50);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', setCanvasSize);
    };
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="fixed top-0 left-0 w-full h-full pointer-events-none opacity-30"
    />
  );
};

const Website = () => {
  const [activeSection, setActiveSection] = useState('home');

  const sections = {
    home: {
      icon: <Building2 className="w-6 h-6" />,
      title: "Home",
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-4">Innovative High-Frequency Trading in EU</h2>
          <p className="text-lg">
            ASI Signal is a leading company in algorithmic trading and high-frequency trading (HFT). 
            Registered in Estonia one of Europe's most digitalized countries, 
            we ensure optimal efficiency and transparency in our operations. 
            We combine advanced technology with sophisticated trading strategies to deliver exceptional results.
          </p>
        </div>
      )
    },
    technology: {
      icon: <Code className="w-6 h-6" />,
      title: "Technology",
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-4">Advanced Trading Technology</h2>
          <p className="text-lg">
            Our proprietary trading platform utilizes cutting-edge technology to:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li className="text-lg">Execute trades with microsecond precision</li>
            <li className="text-lg">Analyze market data in real-time</li>
            <li className="text-lg">Implement sophisticated risk management strategies</li>
          </ul>
        </div>
      )
    },
    security: {
      icon: <Shield className="w-6 h-6" />,
      title: "Security",
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-4">Compliance and Security</h2>
          <p className="text-lg">
            ASI Signal operates under strict supervision of EU financial regulations. 
            We prioritize:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li className="text-lg">Full compliance with EU financial directives</li>
            <li className="text-lg">Robust cybersecurity and data protection</li>
            <li className="text-lg">Transparent reporting and documentation</li>
          </ul>
        </div>
      )
    },
    fintech: {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "FinTech",
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-4">FinTech Innovation</h2>
          <p className="text-lg">
            At the forefront of financial innovation, we combine:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li className="text-lg">Machine learning for market analysis</li>
            <li className="text-lg">High-performance computing systems</li>
            <li className="text-lg">Advanced data analytics</li>
          </ul>
        </div>
      )
    },
    contact: {
      icon: <Globe className="w-6 h-6" />,
      title: "Contact",
      content: (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold mb-4">Contact Us</h2>
          <p className="text-lg">
            ASI Signal
          </p>
          <p className="text-lg">
            Tallinn, Estonia
          </p>
          <p className="text-lg">
            Email: contact@asisignal.com
          </p>
        </div>
      )
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white overflow-hidden">
      <BinaryRainBackground />
      
      <header className="relative bg-black bg-opacity-40 backdrop-blur-lg fixed w-full z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-center">
            <h1 className="text-3xl font-bold">ASI Signal</h1>
          </div>
        </div>
      </header>

      <main className="relative container mx-auto px-4 pt-24 pb-32 z-10">
        <div className="max-w-4xl mx-auto">
          <div className="bg-black bg-opacity-50 backdrop-blur-lg rounded-xl p-8">
            {sections[activeSection].content}
          </div>
        </div>
      </main>

      <div className="fixed bottom-0 w-full bg-black bg-opacity-40 backdrop-blur-lg z-10">
        <div className="container mx-auto">
          <div className="flex justify-around py-4">
            {Object.entries(sections).map(([key, section]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`flex flex-col items-center space-y-2 p-2 rounded-lg transition-colors
                  ${activeSection === key 
                    ? 'bg-blue-600 text-white' 
                    : 'hover:bg-blue-600 hover:bg-opacity-50'}`}
              >
                {section.icon}
                <span className="text-sm">{section.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Website;